import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "framer-motion";
import { ChevronDown, MapPin, Clock, Users, Ticket, Music, Wine, PartyPopper, Car, Building, ChevronLeft, Footprints, Zap, CreditCard, Smile } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

// Animation config
const fadeUp = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

// Shimmer Divider Component
const ShimmerDivider = ({ width = "w-16", color = "#D4AF37" }) => (
  <div className={`relative ${width} h-0.5 mx-auto overflow-hidden`} style={{ background: color }}>
    <motion.div
      className="absolute inset-0"
      style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)' }}
      animate={{ x: ['-100%', '200%'] }}
      transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", repeatDelay: 2 }}
    />
  </div>
);

// Countdown Timer Component
const CountdownTimer = () => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const targetDate = new Date('2026-05-05T15:00:00');
    
    const calculateTimeLeft = () => {
      const now = new Date();
      const difference = targetDate - now;
      
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / (1000 * 60)) % 60),
          seconds: Math.floor((difference / 1000) % 60),
        });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, []);

  const TimeUnit = ({ value, label }) => (
    <div className="text-center">
      <div 
        className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-lg mb-2"
        style={{ background: '#111111', border: '1px solid rgba(212,175,55,0.2)' }}
      >
        <span className="font-heading font-black text-2xl md:text-3xl text-white">{String(value).padStart(2, '0')}</span>
      </div>
      <span className="font-accent text-[10px] md:text-xs uppercase tracking-wider text-white/40">{label}</span>
    </div>
  );

  return (
    <div className="flex justify-center gap-3 md:gap-4">
      <TimeUnit value={timeLeft.days} label="Days" />
      <TimeUnit value={timeLeft.hours} label="Hours" />
      <TimeUnit value={timeLeft.minutes} label="Min" />
      <TimeUnit value={timeLeft.seconds} label="Sec" />
    </div>
  );
};

// Hero Section with Premium Sugar Skull Background
const HeroSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });

  // Premium sugar skull watermark
  const sugarSkullUrl = "https://static.prod-images.emergentagent.com/jobs/8bbf1b5c-1903-4355-95c6-d589de2e5570/images/69c4b957b2b796f7412a1d8087bf6fdacf65c7c9a6f79a7fa98e659e377faeaa.png";

  return (
    <section 
      ref={ref}
      className="min-h-screen flex flex-col items-center justify-center px-4 pt-24 pb-16 relative overflow-hidden"
      style={{ backgroundColor: '#0B0B0F' }}
      data-testid="houston-hero"
    >
      {/* Base layer: Rich black background */}
      <div 
        className="absolute inset-0 z-0"
        style={{ backgroundColor: '#0B0B0F' }}
      />

      {/* Sugar Skull Background Layer - Constant visibility, prominent watermark */}
      <div 
        className="absolute inset-0 z-[1] flex items-center justify-center pointer-events-none"
        style={{ transition: 'none' }}
      >
        {/* Skull container with softer vignette mask */}
        <div 
          className="relative w-full h-full flex items-center justify-center"
          style={{
            maskImage: 'radial-gradient(ellipse 80% 90% at 50% 50%, black 40%, transparent 80%)',
            WebkitMaskImage: 'radial-gradient(ellipse 80% 90% at 50% 50%, black 40%, transparent 80%)',
            transition: 'none',
          }}
        >
          {/* The skull image - higher opacity for visibility, NO transitions */}
          <img
            src={sugarSkullUrl}
            alt=""
            className="absolute skull-watermark"
            style={{
              height: '140%',
              width: 'auto',
              maxWidth: 'none',
              objectFit: 'contain',
              opacity: 0.28,
              filter: 'blur(2px) saturate(0.8)',
              transform: 'translateY(-5%)',
            }}
          />
        </div>
        
        {/* Ambient glow - static */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse 50% 60% at 50% 45%, rgba(212,175,55,0.05) 0%, transparent 60%)',
          }}
        />
      </div>

      {/* Overlay Layer 1: Emerald gradient from top-left - reduced opacity */}
      <div 
        className="absolute inset-0 z-[2] pointer-events-none"
        style={{ 
          background: 'radial-gradient(ellipse 80% 60% at 15% 20%, rgba(15,59,46,0.35) 0%, transparent 50%)',
        }}
      />

      {/* Overlay Layer 2: Deep black fade toward bottom - softer */}
      <div 
        className="absolute inset-0 z-[3] pointer-events-none"
        style={{ 
          background: 'linear-gradient(180deg, transparent 0%, transparent 50%, rgba(11,11,15,0.5) 80%, rgba(11,11,15,0.8) 100%)',
        }}
      />

      {/* Overlay Layer 3: Subtle side vignettes - reduced */}
      <div 
        className="absolute inset-0 z-[4] pointer-events-none"
        style={{ 
          background: 'linear-gradient(90deg, rgba(11,11,15,0.4) 0%, transparent 15%, transparent 85%, rgba(11,11,15,0.4) 100%)',
        }}
      />

      {/* Subtle film grain texture overlay */}
      <div 
        className="absolute inset-0 z-[5] pointer-events-none opacity-[0.03]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'repeat',
        }}
      />

      {/* Back link */}
      <Link 
        to="/" 
        className="absolute top-24 left-6 md:left-10 z-20 flex items-center gap-2 text-white/50 hover:text-white transition-colors"
        data-testid="back-home"
      >
        <ChevronLeft className="w-4 h-4" />
        <span className="font-accent text-xs uppercase tracking-wider">All Cities</span>
      </Link>

      {/* Content Layer - Above all backgrounds */}
      <div className="text-center max-w-4xl mx-auto relative z-10">
        {/* Event badge - staggered animation */}
        <motion.div 
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
          style={{ background: 'rgba(194,78,44,0.2)', border: '1px solid rgba(194,78,44,0.4)' }}
          initial={{ opacity: 0, y: 12 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="w-2 h-2 rounded-full bg-[#C9F400] animate-pulse" />
          <span className="text-terracotta text-xs font-accent uppercase tracking-wider">Tickets Live</span>
        </motion.div>

        {/* Main headline - staggered fade up */}
        <motion.h1 
          className="font-heading font-black text-5xl sm:text-6xl md:text-8xl lg:text-9xl uppercase tracking-tight leading-[0.85] mb-4"
          initial={{ opacity: 0, y: 15 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        >
          <span style={{ color: '#F3EFE6' }}>CINCO DE </span>
          <span className="text-terracotta">CRAWL</span>
        </motion.h1>
        
        <motion.h2 
          className="font-heading font-black text-4xl sm:text-5xl md:text-7xl uppercase tracking-tight mb-6"
          style={{ color: '#C24E2C' }}
          initial={{ opacity: 0, y: 15 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
        >
          HOUSTON
        </motion.h2>

        {/* Gold divider */}
        <motion.div
          initial={{ opacity: 0, scaleX: 0 }}
          animate={inView ? { opacity: 1, scaleX: 1 } : {}}
          transition={{ duration: 0.5, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
        >
          <ShimmerDivider width="w-20" />
        </motion.div>

        {/* Event details */}
        <motion.div 
          className="flex flex-wrap items-center justify-center gap-6 mt-6 mb-8 text-white/70"
          initial={{ opacity: 0, y: 10 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-terracotta" />
            <span className="font-accent text-sm uppercase tracking-wider">May 5, 2026</span>
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-terracotta" />
            <span className="font-accent text-sm uppercase tracking-wider">Downtown Houston</span>
          </div>
        </motion.div>

        {/* CTAs - refined hover states */}
        <motion.div 
          className="flex flex-col sm:flex-row gap-4 justify-center mb-12"
          initial={{ opacity: 0, y: 10 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <Button 
            className="bg-[#C9F400] hover:bg-[#D8FF40] text-[#0B0B0F] px-12 py-7 text-base font-accent font-bold rounded-sm transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_4px_24px_rgba(201,244,0,0.25)]"
            data-testid="get-tickets-btn"
          >
            <Ticket className="w-5 h-5 mr-2" />
            GET TICKETS
          </Button>
          <Button 
            className="bg-transparent border border-white/30 hover:bg-white/5 hover:border-white/50 text-white px-10 py-7 text-base font-accent font-bold rounded-sm transition-all duration-300"
            data-testid="vip-access-btn"
          >
            VIP ACCESS
          </Button>
        </motion.div>

        {/* Countdown Timer */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <CountdownTimer />
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div 
        className="absolute bottom-8 z-10"
        initial={{ opacity: 0 }}
        animate={inView ? { opacity: 1, y: [0, 6, 0] } : {}}
        transition={{ 
          opacity: { duration: 0.5, delay: 0.9 },
          y: { duration: 2, repeat: Infinity, delay: 1 }
        }}
      >
        <ChevronDown className="w-6 h-6 text-white/40" />
      </motion.div>
    </section>
  );
};

// How The Night Flows - Timeline Section
const TimelineSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  const timeline = [
    { time: "3PM", label: "CHECK-IN", desc: "Grab your wristband" },
    { time: "4PM", label: "CRAWL BEGINS", desc: "First drinks flow" },
    { time: "8PM", label: "PEAK ENERGY", desc: "The party peaks" },
    { time: "10PM", label: "LATE NIGHT MODE", desc: "Keep it going" },
  ];

  return (
    <section 
      ref={ref}
      className="py-20 md:py-28 px-4 relative"
      style={{ background: '#050505' }}
      data-testid="timeline-section"
    >
      <div className="max-w-4xl mx-auto">
        <motion.div 
          className="text-center mb-12"
          {...fadeUp}
          animate={inView ? fadeUp.animate : fadeUp.initial}
        >
          <h2 className="font-heading font-black text-4xl md:text-5xl uppercase tracking-tight mb-4" style={{ color: '#F4F1E8' }}>
            HOW THE NIGHT <span className="text-terracotta">FLOWS</span>
          </h2>
          <ShimmerDivider />
        </motion.div>

        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-1/2 top-0 bottom-0 w-px bg-white/10 -translate-x-1/2 hidden md:block" />
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-4">
            {timeline.map((item, i) => (
              <motion.div
                key={item.time}
                className="text-center relative"
                initial={{ opacity: 0, y: 16 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: i * 0.12, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              >
                {/* Time badge */}
                <div 
                  className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 relative"
                  style={{ 
                    background: 'linear-gradient(135deg, rgba(212,175,55,0.15) 0%, rgba(194,78,44,0.15) 100%)',
                    border: '1px solid rgba(212,175,55,0.3)',
                  }}
                >
                  <span className="font-heading font-black text-lg text-white">{item.time}</span>
                </div>
                <h3 className="font-accent font-bold text-sm uppercase tracking-wider mb-1 text-terracotta">
                  {item.label}
                </h3>
                <p className="text-white/40 text-xs">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// What You Get Section
const WhatYouGetSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  const perks = [
    { icon: Building, title: "8+ VENUES", desc: "Participating bars" },
    { icon: Ticket, title: "ONE WRISTBAND", desc: "All access. All night." },
    { icon: Wine, title: "DRINK SPECIALS", desc: "Exclusive pricing" },
    { icon: PartyPopper, title: "LATE NIGHT", desc: "Keep the party going" },
  ];

  return (
    <section 
      ref={ref}
      className="py-20 md:py-28 px-4 relative overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #0F3B2E 0%, #0A2E24 100%)' }}
      data-testid="what-you-get"
    >
      <div className="max-w-5xl mx-auto relative z-10">
        <motion.div 
          className="text-center mb-12"
          {...fadeUp}
          animate={inView ? fadeUp.animate : fadeUp.initial}
        >
          <h2 className="font-heading font-black text-4xl md:text-5xl uppercase tracking-tight mb-4" style={{ color: '#F4F1E8' }}>
            WHAT YOU <span className="text-terracotta">GET</span>
          </h2>
          <ShimmerDivider />
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
          {perks.map((perk, i) => (
            <motion.div
              key={perk.title}
              className="text-center"
              initial={{ opacity: 0, y: 16 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: i * 0.1, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="w-14 h-14 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ background: 'rgba(194,78,44,0.15)' }}>
                <perk.icon className="w-6 h-6 text-terracotta" />
              </div>
              <h3 className="font-accent font-bold text-sm md:text-base uppercase tracking-wider mb-1" style={{ color: '#F4F1E8' }}>
                {perk.title}
              </h3>
              <p className="text-white/40 text-xs md:text-sm">{perk.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// What You'll Need Section
const WhatYouNeedSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  const items = [
    { icon: CreditCard, title: "VALID 21+ ID", desc: "Government issued" },
    { icon: Ticket, title: "YOUR TICKET", desc: "Digital or printed" },
    { icon: Footprints, title: "COMFORTABLE SHOES", desc: "You'll be moving" },
    { icon: Smile, title: "GOOD ENERGY", desc: "Bring the vibes" },
  ];

  return (
    <section 
      ref={ref}
      className="py-16 md:py-24 px-4 relative"
      style={{ background: '#050505' }}
      data-testid="what-you-need"
    >
      <div className="max-w-3xl mx-auto">
        <motion.div 
          className="text-center mb-10"
          {...fadeUp}
          animate={inView ? fadeUp.animate : fadeUp.initial}
        >
          <h2 className="font-heading font-black text-3xl md:text-4xl uppercase tracking-tight mb-4" style={{ color: '#F4F1E8' }}>
            WHAT YOU'LL <span className="text-terracotta">NEED</span>
          </h2>
          <ShimmerDivider width="w-12" />
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {items.map((item, i) => (
            <motion.div
              key={item.title}
              className="text-center p-4 rounded-lg"
              style={{ background: '#111111' }}
              initial={{ opacity: 0, y: 16 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: i * 0.08, duration: 0.5 }}
            >
              <item.icon className="w-6 h-6 mx-auto mb-3 text-[#D4AF37]" />
              <h3 className="font-accent font-bold text-xs uppercase tracking-wider mb-1 text-white/80">
                {item.title}
              </h3>
              <p className="text-white/30 text-[10px]">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Featured Venues Section
const VenuesSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  const venues = [
    { name: "Heights Social", vibe: "Rooftop views. Tequila flights. Main character energy." },
    { name: "Sidebar", vibe: "Hip-hop all day. Bottle service vibes." },
    { name: "Clé", vibe: "Vegas-level production. Pure nightlife." },
    { name: "Prospect Park", vibe: "Multi-level chaos. Something for everyone." },
    { name: "Concrete Cowboy", vibe: "Country meets club. Boot-stomping anthems." },
    { name: "Gaslamp", vibe: "Downtown institution. Classic Houston energy." },
  ];

  return (
    <section 
      ref={ref}
      className="py-20 md:py-28 px-4 relative"
      style={{ background: '#050505' }}
      data-testid="venues-section"
    >
      <div className="max-w-5xl mx-auto">
        <motion.div 
          className="text-center mb-12"
          {...fadeUp}
          animate={inView ? fadeUp.animate : fadeUp.initial}
        >
          <h2 className="font-heading font-black text-4xl md:text-5xl uppercase tracking-tight mb-4" style={{ color: '#F4F1E8' }}>
            FEATURED <span className="text-terracotta">VENUES</span>
          </h2>
          <ShimmerDivider />
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {venues.map((venue, i) => (
            <motion.div
              key={venue.name}
              className="group p-6 rounded-lg transition-all duration-300"
              style={{ 
                background: '#111111',
                boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
              }}
              initial={{ opacity: 0, y: 16 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: i * 0.08, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ 
                y: -4,
                boxShadow: '0 8px 32px rgba(0,0,0,0.4), 0 0 20px rgba(212,175,55,0.08)',
              }}
            >
              <div className="w-8 h-0.5 mb-4" style={{ background: '#D4AF37' }} />
              <h3 className="font-heading font-bold text-xl md:text-2xl uppercase mb-2 text-white group-hover:text-terracotta transition-colors">
                {venue.name}
              </h3>
              <p className="text-white/40 text-sm leading-relaxed">{venue.vibe}</p>
            </motion.div>
          ))}
        </div>

        <motion.p 
          className="text-center text-white/30 text-xs mt-8"
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 0.5 }}
        >
          + More venues to be announced
        </motion.p>
      </div>
    </section>
  );
};

// Location Section - Enhanced Interactive Map
const LocationSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const [activePin, setActivePin] = useState(null);

  // Venue pins with positions (percentage-based)
  const venuePins = [
    { id: 'heights', name: "Heights Social", vibe: "Rooftop tequila bar", x: 35, y: 28 },
    { id: 'sidebar', name: "Sidebar", vibe: "Hip-hop vibes", x: 55, y: 42 },
    { id: 'cle', name: "Clé", vibe: "Vegas-level production", x: 68, y: 55 },
    { id: 'prospect', name: "Prospect Park", vibe: "Multi-level madness", x: 42, y: 65 },
    { id: 'gaslamp', name: "Gaslamp", vibe: "Downtown institution", x: 58, y: 72 },
  ];

  // Check-in location
  const checkinPin = { id: 'checkin', x: 48, y: 38 };

  const handlePinClick = (pin) => {
    setActivePin(activePin?.id === pin.id ? null : pin);
  };

  // Pin component
  const VenuePin = ({ pin, isActive }) => (
    <button
      className="absolute -translate-x-1/2 -translate-y-1/2 cursor-pointer focus:outline-none z-20 group"
      style={{ left: `${pin.x}%`, top: `${pin.y}%` }}
      onClick={() => handlePinClick(pin)}
      onMouseEnter={() => setActivePin(pin)}
      onMouseLeave={() => setActivePin(null)}
      aria-label={`View ${pin.name}`}
    >
      {/* Glow effect */}
      <motion.div 
        className="absolute rounded-full"
        style={{ 
          width: 24, height: 24, 
          left: '50%', top: '50%', 
          transform: 'translate(-50%, -50%)', 
          background: 'radial-gradient(circle, rgba(212,175,55,0.4) 0%, transparent 70%)',
        }}
        animate={{ opacity: isActive ? 1 : 0, scale: isActive ? 1.2 : 1 }}
        transition={{ duration: 0.15 }}
      />
      {/* Slow gold outer glow pulse */}
      <motion.div 
        className="absolute rounded-full pointer-events-none"
        style={{ 
          width: 18, height: 18, 
          left: '50%', top: '50%', 
          transform: 'translate(-50%, -50%)', 
          border: '1px solid rgba(212,175,55,0.25)',
        }}
        animate={{ 
          scale: [1, 1.6, 1], 
          opacity: [0.4, 0, 0.4],
        }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: pin.id * 0.3 }}
      />
      {/* Pin: gold ring + terracotta center */}
      <div 
        className="relative w-3 h-3 md:w-3.5 md:h-3.5 rounded-full flex items-center justify-center transition-all duration-200 group-hover:scale-125"
        style={{ 
          background: 'linear-gradient(135deg, #D4AF37, #B8963A)',
          boxShadow: isActive ? '0 0 12px rgba(212,175,55,0.6)' : '0 1px 4px rgba(0,0,0,0.4)',
          transform: isActive ? 'scale(1.2)' : 'scale(1)',
        }}
      >
        <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#C24E2C' }} />
      </div>
    </button>
  );

  // Check-in pin (distinct styling)
  const CheckinPin = ({ isActive }) => (
    <button
      className="absolute -translate-x-1/2 -translate-y-1/2 cursor-pointer focus:outline-none z-30"
      style={{ left: `${checkinPin.x}%`, top: `${checkinPin.y}%` }}
      onClick={() => handlePinClick({ ...checkinPin, name: 'Check-in / Wristband Pickup', vibe: 'Location announced soon', isCheckin: true })}
      onMouseEnter={() => setActivePin({ ...checkinPin, name: 'Check-in / Wristband Pickup', vibe: 'Location announced soon', isCheckin: true })}
      onMouseLeave={() => setActivePin(null)}
      aria-label="Check-in location"
    >
      {/* Pulse animation for check-in */}
      <motion.div 
        className="absolute rounded-full"
        style={{ 
          width: 32, height: 32, 
          left: '50%', top: '50%', 
          transform: 'translate(-50%, -50%)', 
          background: 'rgba(201,244,0,0.2)',
        }}
        animate={{ scale: [1, 1.6, 1], opacity: [0.4, 0, 0.4] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }}
      />
      {/* Pin: neon green */}
      <div 
        className="relative w-4 h-4 md:w-5 md:h-5 rounded-full flex items-center justify-center"
        style={{ 
          background: '#C9F400',
          boxShadow: '0 0 12px rgba(201,244,0,0.4)',
        }}
      >
        <div className="w-2 h-2 rounded-full bg-[#0B0B0F]" />
      </div>
    </button>
  );

  // Tooltip component
  const PinTooltip = ({ pin }) => {
    if (!pin) return null;
    
    // Calculate tooltip position to prevent clipping
    const tooltipStyle = {
      left: `${pin.x}%`,
      top: `${pin.y}%`,
      transform: `translate(-50%, calc(-100% - 16px))`,
    };

    // Adjust if too close to edges
    if (pin.y < 25) {
      tooltipStyle.transform = `translate(-50%, 20px)`;
    }

    return (
      <motion.div
        className="absolute pointer-events-none z-50"
        style={tooltipStyle}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 6 }}
        transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
      >
        <div 
          className="px-3 py-2 rounded text-center whitespace-nowrap"
          style={{ 
            background: pin.isCheckin ? 'rgba(201,244,0,0.95)' : '#141414', 
            border: pin.isCheckin ? 'none' : '1px solid rgba(212,175,55,0.3)', 
            boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
            color: pin.isCheckin ? '#0B0B0F' : '#F4F1E8',
          }}
        >
          <p className="font-accent font-bold text-xs uppercase tracking-wide">
            {pin.name}
          </p>
          {pin.vibe && (
            <p className={`text-[10px] mt-0.5 ${pin.isCheckin ? 'text-[#0B0B0F]/70' : 'text-white/50'}`}>
              {pin.vibe}
            </p>
          )}
        </div>
        {/* Arrow */}
        <div 
          className="absolute left-1/2 -translate-x-1/2"
          style={{ 
            bottom: pin.y < 25 ? 'auto' : -4,
            top: pin.y < 25 ? -4 : 'auto',
            borderLeft: '4px solid transparent',
            borderRight: '4px solid transparent',
            borderTop: pin.y < 25 ? 'none' : `4px solid ${pin.isCheckin ? 'rgba(201,244,0,0.95)' : '#141414'}`,
            borderBottom: pin.y < 25 ? `4px solid ${pin.isCheckin ? 'rgba(201,244,0,0.95)' : '#141414'}` : 'none',
          }}
        />
      </motion.div>
    );
  };

  return (
    <section 
      ref={ref}
      className="py-20 md:py-28 px-4 relative"
      style={{ background: 'linear-gradient(180deg, #0A2E24 0%, #0F3B2E 50%, #0A2E24 100%)' }}
      data-testid="location-section"
    >
      <div className="max-w-3xl mx-auto text-center">
        <motion.div
          {...fadeUp}
          animate={inView ? fadeUp.animate : fadeUp.initial}
        >
          <h2 className="font-heading font-black text-4xl md:text-5xl uppercase tracking-tight mb-4" style={{ color: '#F4F1E8' }}>
            WHERE IT'S <span className="text-terracotta">GOING DOWN</span>
          </h2>
          <ShimmerDivider />
          
          <p className="text-white/60 text-lg md:text-xl mt-8 mb-4 max-w-xl mx-auto font-light">
            All venues within a tight downtown radius.
          </p>
          <p className="text-white/40 text-sm mb-10">
            Walk it. Ride it. Own it.
          </p>

          {/* Enhanced Interactive Map */}
          <div 
            className="relative mx-auto max-w-lg aspect-[4/3] rounded-lg"
            style={{ 
              background: 'linear-gradient(135deg, #0F0F0F 0%, #1A1A1A 50%, #141414 100%)',
              border: '1px solid rgba(255,255,255,0.06)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
            }}
          >
            {/* Subtle grid lines */}
            <div className="absolute inset-0 opacity-[0.04] rounded-lg overflow-hidden">
              {[...Array(8)].map((_, i) => (
                <div key={`h-${i}`} className="absolute w-full h-px bg-white" style={{ top: `${(i + 1) * 11.1}%` }} />
              ))}
              {[...Array(10)].map((_, i) => (
                <div key={`v-${i}`} className="absolute h-full w-px bg-white" style={{ left: `${(i + 1) * 10}%` }} />
              ))}
            </div>
            
            {/* Crawl District Overlay - soft highlight zone */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <motion.div
                className="rounded-full"
                style={{
                  width: '55%',
                  height: '65%',
                  background: 'radial-gradient(ellipse at center, rgba(212,175,55,0.06) 0%, rgba(212,175,55,0.02) 50%, transparent 70%)',
                  border: '1px solid rgba(212,175,55,0.08)',
                }}
                animate={{ opacity: [0.6, 0.9, 0.6] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              />
            </div>

            {/* District label */}
            <div className="absolute top-3 left-1/2 -translate-x-1/2 pointer-events-none">
              <span 
                className="font-accent text-[9px] md:text-[10px] uppercase tracking-[0.2em] px-3 py-1 rounded-full"
                style={{ background: 'rgba(212,175,55,0.1)', color: 'rgba(212,175,55,0.6)' }}
              >
                Downtown Crawl District
              </span>
            </div>

            {/* Check-in Pin */}
            <CheckinPin isActive={activePin?.isCheckin} />

            {/* Venue Pins */}
            {venuePins.map((pin) => (
              <VenuePin key={pin.id} pin={pin} isActive={activePin?.id === pin.id} />
            ))}

            {/* Tooltip */}
            <AnimatePresence>
              {activePin && <PinTooltip pin={activePin} />}
            </AnimatePresence>
            
            {/* Bottom label */}
            <div className="absolute bottom-3 left-3 text-left">
              <p className="font-accent text-[9px] uppercase tracking-wider text-white/30">Downtown</p>
              <p className="font-heading font-bold text-white/80 text-xs">Houston, TX</p>
            </div>

            {/* Legend */}
            <div className="absolute bottom-3 right-3 flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full" style={{ background: '#C9F400' }} />
                <span className="text-[8px] text-white/40 uppercase tracking-wider">Check-in</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full" style={{ background: 'linear-gradient(135deg, #D4AF37, #B8963A)' }} />
                <span className="text-[8px] text-white/40 uppercase tracking-wider">Venues</span>
              </div>
            </div>
          </div>

          {/* Helpful text below map */}
          <p className="text-white/40 text-xs mt-6">
            Most venues are within a 10–15 minute walk. Short rides between clusters.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// Hotels Section - Upgraded with walking times
const HotelsSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  const hotels = [
    { name: "Marriott Downtown", walk: "5 min walk" },
    { name: "AC Hotel Houston", walk: "8 min walk" },
    { name: "Hyatt Place Midtown", walk: "12 min walk" },
  ];

  return (
    <section 
      ref={ref}
      className="py-16 md:py-24 px-4 relative"
      style={{ background: '#050505' }}
      data-testid="hotels-section"
    >
      <div className="max-w-2xl mx-auto text-center">
        <motion.div
          {...fadeUp}
          animate={inView ? fadeUp.animate : fadeUp.initial}
        >
          <ShimmerDivider width="w-12" />
          
          <h2 className="font-heading font-black text-3xl md:text-4xl uppercase tracking-tight mb-4 mt-8" style={{ color: '#F4F1E8' }}>
            STAY CLOSE. <span className="text-terracotta">PARTY SMART.</span>
          </h2>
          
          <p className="text-white/40 text-sm mb-8">Recommended hotels near the action</p>

          <div className="flex flex-col gap-3">
            {hotels.map((hotel, i) => (
              <motion.div
                key={hotel.name}
                className="py-4 px-6 rounded-sm flex items-center justify-between"
                style={{ background: '#111111' }}
                initial={{ opacity: 0, x: -20 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ delay: i * 0.1, duration: 0.4 }}
              >
                <span className="font-accent text-sm uppercase tracking-wider text-white/70">{hotel.name}</span>
                <span className="font-accent text-xs uppercase tracking-wider text-[#D4AF37]">{hotel.walk}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Ride Section
const RideSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section 
      ref={ref}
      className="py-16 md:py-24 px-4 relative"
      style={{ background: '#0A0A0A' }}
      data-testid="ride-section"
    >
      <div className="max-w-2xl mx-auto text-center">
        <motion.div
          {...fadeUp}
          animate={inView ? fadeUp.animate : fadeUp.initial}
        >
          <h2 className="font-heading font-black text-3xl md:text-4xl uppercase tracking-tight mb-4" style={{ color: '#F4F1E8' }}>
            PLAN YOUR <span className="text-terracotta">RIDE.</span>
          </h2>
          <ShimmerDivider width="w-12" />
          
          <p className="text-white/50 text-base mt-8 mb-8 max-w-md mx-auto font-light">
            Drink responsibly.<br />
            Use Uber or Lyft.<br />
            Plan your night ahead.
          </p>

          {/* Ride icons */}
          <div className="flex justify-center gap-6">
            <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: '#111111' }}>
              <Car className="w-5 h-5 text-white/40" />
            </div>
            <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: '#111111' }}>
              <Users className="w-5 h-5 text-white/40" />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      q: "Is this 21+?",
      a: "Yes. Valid government-issued ID required at check-in. No exceptions."
    },
    {
      q: "What does the wristband include?",
      a: "Free entry to all participating venues, exclusive drink specials at every bar, and access to late-night programming."
    },
    {
      q: "Refund policy?",
      a: "All ticket sales are final. No refunds or exchanges. Tickets are transferable to another person."
    },
    {
      q: "Can I transfer my ticket?",
      a: "Yes. You can transfer your ticket to someone else before the event. Contact us for details."
    },
    {
      q: "What time does check-in start?",
      a: "Check-in opens at 3:00 PM at the designated starting venue. The crawl officially kicks off at 4:00 PM."
    },
  ];

  return (
    <section 
      ref={ref}
      className="py-20 md:py-28 px-4 relative"
      style={{ background: '#050505' }}
      data-testid="faq-section"
    >
      <div className="max-w-2xl mx-auto">
        <motion.div 
          className="text-center mb-12"
          {...fadeUp}
          animate={inView ? fadeUp.animate : fadeUp.initial}
        >
          <h2 className="font-heading font-black text-4xl md:text-5xl uppercase tracking-tight mb-4" style={{ color: '#F4F1E8' }}>
            <span className="text-terracotta">FAQ</span>
          </h2>
          <ShimmerDivider />
        </motion.div>

        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <motion.div
              key={i}
              className="rounded-lg overflow-hidden"
              style={{ background: '#111111' }}
              initial={{ opacity: 0, y: 16 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: i * 0.08, duration: 0.5 }}
            >
              <button
                className="w-full px-6 py-5 flex items-center justify-between text-left"
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                data-testid={`faq-${i}`}
              >
                <span className="font-accent font-bold text-sm uppercase tracking-wider text-white/80">
                  {faq.q}
                </span>
                <ChevronDown 
                  className={`w-5 h-5 text-terracotta transition-transform duration-300 ${openIndex === i ? 'rotate-180' : ''}`} 
                />
              </button>
              <motion.div
                initial={false}
                animate={{ height: openIndex === i ? 'auto' : 0, opacity: openIndex === i ? 1 : 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <p className="px-6 pb-5 text-white/50 text-sm leading-relaxed">
                  {faq.a}
                </p>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Don't Miss Houston Section - NEW
const DontMissSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section 
      ref={ref}
      className="py-24 md:py-32 px-4 relative"
      style={{ background: 'linear-gradient(180deg, #0A2E24 0%, #0F3B2E 100%)' }}
      data-testid="dont-miss-section"
    >
      <div className="max-w-3xl mx-auto text-center relative z-10">
        <motion.div
          {...fadeUp}
          animate={inView ? fadeUp.animate : fadeUp.initial}
        >
          <ShimmerDivider width="w-16" />
          
          <h2 className="font-heading font-black text-5xl md:text-7xl lg:text-8xl uppercase tracking-tight mb-6 mt-10 leading-[0.85]" style={{ color: '#F4F1E8' }}>
            DON'T MISS<br /><span className="text-terracotta">HOUSTON.</span>
          </h2>
          
          <p className="text-white/50 text-lg md:text-xl mb-10 font-light">
            One night. One wristband. Unlimited energy.
          </p>
          
          <Button 
            className="bg-[#C9F400] hover:bg-[#D8FF40] text-[#0B0B0F] px-14 py-8 text-lg font-accent font-bold rounded-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(201,244,0,0.35)]"
            data-testid="dont-miss-tickets"
          >
            GET TICKETS
          </Button>
          
          <ShimmerDivider width="w-16" className="mt-10" />
        </motion.div>
      </div>
    </section>
  );
};

// Final CTA Section
const FinalCTASection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section 
      ref={ref}
      className="py-24 md:py-32 px-4 relative"
      style={{ background: '#050505' }}
      data-testid="final-cta"
    >
      {/* Terracotta vignette */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 80% 60% at 50% 100%, rgba(194,78,44,0.06) 0%, transparent 60%)',
        }}
      />
      
      <div className="max-w-3xl mx-auto text-center relative z-10">
        <motion.div
          {...fadeUp}
          animate={inView ? fadeUp.animate : fadeUp.initial}
        >
          <ShimmerDivider width="w-16" />
          
          <h2 className="font-heading font-black text-4xl md:text-6xl lg:text-7xl uppercase tracking-tight mb-8 mt-10 leading-[0.9]" style={{ color: '#F4F1E8' }}>
            READY TO LOCK<br />YOUR <span className="text-terracotta">SPOT?</span>
          </h2>
          
          <Button 
            className="bg-[#C9F400] hover:bg-[#D8FF40] text-[#0B0B0F] px-14 py-8 text-lg font-accent font-bold rounded-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(201,244,0,0.35)]"
            data-testid="final-get-tickets"
          >
            GET HOUSTON TICKETS
          </Button>
          
          <ShimmerDivider width="w-16" className="mt-10" />
        </motion.div>
      </div>
    </section>
  );
};

// Legal Footer
const LegalFooter = () => (
  <div className="py-8 px-4 text-center" style={{ background: '#050505' }} data-testid="legal-footer">
    <p className="text-white/20 text-xs max-w-lg mx-auto leading-relaxed">
      21+ with valid government ID required. Participating venues subject to change. Event rain or shine.
    </p>
  </div>
);

// Main Houston Page Component
const HoustonLivePage = () => {
  return (
    <div className="min-h-screen" style={{ background: '#050505' }}>
      <HeroSection />
      <TimelineSection />
      <WhatYouGetSection />
      <WhatYouNeedSection />
      <VenuesSection />
      <LocationSection />
      <HotelsSection />
      <RideSection />
      <FAQSection />
      <DontMissSection />
      <FinalCTASection />
      <LegalFooter />
    </div>
  );
};

export default HoustonLivePage;
